import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
    Table,
    TableHead,
    TableBody,
    TableCell,
    TableRow,
    TableContainer,
    Paper,
    IconButton,
    Menu,
    MenuItem,
    TextField,
    Box,
    Typography,
    InputAdornment,
    Snackbar,
    Alert,
    Chip,
    Avatar,
    Stack,
    Pagination,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Tooltip
} from "@mui/material";
import { MdMoreVert, MdSearch, MdCheckCircle, MdCancel, MdDelete, MdVerifiedUser } from "react-icons/md";
import { fetchUsers, updateUserStatus, deleteUser } from "../../api/adminApi";
import "./PendingApprovalTable.css";

// Helper to generate a color from a name for the Avatar
const stringToColor = (string) => {
    let hash = 0;
    for (let i = 0; i < string.length; i++) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }
    let color = '#';
    for (let i = 0; i < 3; i++) {
        const value = (hash >> (i * 8)) & 0xff;
        color += `00${value.toString(16)}`.slice(-2);
    }
    return color;
};

const PendingApprovalTable = () => {
    const [pendingUsers, setPendingUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuUserId, setMenuUserId] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    // Confirmation Dialog State
    const [confirmDialog, setConfirmDialog] = useState({ open: false, action: null, userId: null, title: "", content: "" });

    const usersPerPage = 7;

    // --- Data Fetching Logic ---
    const loadPendingUsers = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetchUsers();
            const data = res.data || [];
            const filtered = data.filter((u) => (u.status || "").toLowerCase() === "pending");
            setPendingUsers(filtered);
            setCurrentPage(1);
        } catch (err) {
            console.error("Error fetching users:", err);
            showSnackbar("Failed to load users.", "error");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadPendingUsers();
    }, [loadPendingUsers]);

    const showSnackbar = (message, severity) => {
        setSnackbar({ open: true, message, severity });
    };

    const filteredUsers = useMemo(() => {
        if (!searchTerm) return pendingUsers;
        const lower = searchTerm.toLowerCase();
        return pendingUsers.filter(
            (u) =>
                u.name.toLowerCase().includes(lower) ||
                u.email.toLowerCase().includes(lower) ||
                (u.course_name && u.course_name.toLowerCase().includes(lower))
        );
    }, [searchTerm, pendingUsers]);

    const totalPages = Math.ceil(filteredUsers.length / usersPerPage);
    const currentUsers = filteredUsers.slice(
        (currentPage - 1) * usersPerPage,
        currentPage * usersPerPage
    );

    // --- Menu Handlers ---
    const handleMenuOpen = (event, userId) => {
        setAnchorEl(event.currentTarget);
        setMenuUserId(userId);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuUserId(null);
    };

    // --- Action Handlers ---
    const initiateAction = (userId, action) => {
        handleMenuClose();
        let title = "";
        let content = "";

        if (action === "approve") {
            performAction(userId, "approve");
            return;
        }

        if (action === "reject") {
            title = "Reject User Application?";
            content = "This will reject the user's application. They will be notified via email.";
        } else if (action === "delete") {
            title = "Delete User Permanently?";
            content = "This action cannot be undone. The user data will be removed from the system.";
        }

        setConfirmDialog({ open: true, action, userId, title, content });
    };

    const performAction = async (userId, action) => {
        try {
            if (action === "approve") {
                await updateUserStatus(userId, "approved");
                showSnackbar("User approved successfully! 🎉", "success");
            } else if (action === "reject") {
                await updateUserStatus(userId, "rejected");
                showSnackbar("User application rejected.", "warning");
            } else if (action === "delete") {
                await deleteUser(userId);
                showSnackbar("User deleted permanently.", "error");
            }
            await loadPendingUsers();
        } catch (err) {
            console.error("Error performing action:", err);
            showSnackbar("Action failed. Please try again.", "error");
        }
    };

    const handleConfirmClose = (confirmed) => {
        if (confirmed && confirmDialog.userId) {
            performAction(confirmDialog.userId, confirmDialog.action);
        }
        setConfirmDialog({ ...confirmDialog, open: false });
    };

    return (
        <Box className="table-wrapper">
            {/* Header Section */}
            <Paper elevation={0} className="table-header-paper">
                <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                    <Box>
                        <Typography variant="h5" fontWeight="600" color="text.primary" display="flex" alignItems="center" gap={1}>
                            <MdVerifiedUser color="#1976d2" /> Pending Approvals
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Manage user access requests and course enrollments.
                        </Typography>
                    </Box>

                    <TextField
                        size="small"
                        placeholder="Search by name, email..."
                        variant="outlined"
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setCurrentPage(1);
                        }}
                        className="search-field"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <MdSearch color="#888" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Box>
            </Paper>

            {/* Table Section */}
            <Paper elevation={0} className="table-content-paper">
                <TableContainer>
                    <Table sx={{ minWidth: 650 }} aria-label="pending users table">
                        <TableHead>
                            <TableRow className="table-header-row">
                                <TableCell>User Profile</TableCell>
                                <TableCell>Contact Info</TableCell>
                                <TableCell>Target Course</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Date Requested</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                                        <CircularProgress size={40} />
                                        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>Loading requests...</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : currentUsers.length > 0 ? (
                                currentUsers.map((user) => (
                                    <TableRow key={user.id} hover className="data-row">
                                        <TableCell>
                                            <Stack direction="row" alignItems="center" spacing={2}>
                                                <Avatar sx={{ bgcolor: stringToColor(user.name), width: 36, height: 36, fontSize: '0.9rem' }}>
                                                    {user.name.charAt(0).toUpperCase()}
                                                </Avatar>
                                                <Typography variant="subtitle2" fontWeight="500">{user.name}</Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">{user.email}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={user.course_name || "General Access"}
                                                size="small"
                                                variant="outlined"
                                                sx={{ borderColor: '#e0e0e0', color: 'text.primary' }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={user.status}
                                                size="small"
                                                color="warning"
                                                className="status-chip-pending"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" color="text.secondary">
                                                {new Date(user.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                                            </Typography>
                                            <Typography variant="caption" display="block" color="text.disabled">
                                                {new Date(user.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                            </Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Manage Request">
                                                <IconButton onClick={(e) => handleMenuOpen(e, user.id)} size="small">
                                                    <MdMoreVert />
                                                </IconButton>
                                            </Tooltip>
                                            <Menu
                                                anchorEl={anchorEl}
                                                open={menuUserId === user.id}
                                                onClose={handleMenuClose}
                                                PaperProps={{
                                                    elevation: 3,
                                                    sx: { minWidth: '140px', borderRadius: '8px', mt: 1 }
                                                }}
                                            >
                                                <MenuItem onClick={() => initiateAction(user.id, "approve")} sx={{ fontSize: '0.9rem', gap: 1 }}>
                                                    <MdCheckCircle color="green" /> Approve
                                                </MenuItem>
                                                <MenuItem onClick={() => initiateAction(user.id, "reject")} sx={{ fontSize: '0.9rem', gap: 1 }}>
                                                    <MdCancel color="orange" /> Reject
                                                </MenuItem>
                                                <MenuItem onClick={() => initiateAction(user.id, "delete")} sx={{ fontSize: '0.9rem', gap: 1, color: 'error.main' }}>
                                                    <MdDelete /> Delete
                                                </MenuItem>
                                            </Menu>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                                        <Box display="flex" flexDirection="column" alignItems="center">
                                            <MdVerifiedUser size={48} color="#e0e0e0" />
                                            <Typography variant="body1" sx={{ mt: 1, color: 'text.secondary' }}>
                                                No pending approvals found.
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                {!loading && filteredUsers.length > 0 && (
                    <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #f0f0f0' }}>
                        <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={(e, v) => setCurrentPage(v)}
                            color="primary"
                            shape="rounded"
                        />
                    </Box>
                )}
            </Paper>

            {/* Confirmation Dialog */}
            <Dialog
                open={confirmDialog.open}
                onClose={() => handleConfirmClose(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title" sx={{ fontWeight: 600 }}>
                    {confirmDialog.title}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {confirmDialog.content}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => handleConfirmClose(false)} color="inherit" variant="text">
                        Cancel
                    </Button>
                    <Button
                        onClick={() => handleConfirmClose(true)}
                        color={confirmDialog.action === 'delete' ? "error" : "primary"}
                        variant="contained"
                        autoFocus
                        disableElevation
                    >
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Notifications */}
            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} variant="filled" sx={{ width: "100%", boxShadow: 3 }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default PendingApprovalTable;
